import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  createPdf,
  fetchOrders,
  searchOrderById,
} from "../../../../redux/Thunk/Order/OrderThunks";
import Loader from "../../../Loader";
import Pagination from "../../../common/Pagination";
import Close from "../../../common/icons/Close";
import Search from "../../../common/icons/Search";
import AdminTable from "../../../common/Table";
import toast from "react-hot-toast";

const Orders = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(15);
  const LoadingStatus = useSelector((state) => state?.order?.loading);
  const { data } = useSelector((state) => {
    return {
      data: state?.order?.orders,
    };
  });
  useEffect(() => {
    if (
      !debouncedSearchTerm?.length &&
      (currentPage >= 1 || !data?.orders?.length)
    ) {
      dispatch(fetchOrders({ page: currentPage, limit, filterType: "all" }));
    }
  }, [dispatch, currentPage, limit, debouncedSearchTerm, data?.orders?.length]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const totalPages = data?.totalPages;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "00:00";
    const date = new Date(timeString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleCreatePdf = useCallback(async (order) => {
    const payload = {
      patient: {
        phoneNumber: order?.patientDetail?.phoneNumber || "",
        name: order?.patientDetail?.name || "",
        surname: order?.patientDetail?.surname || "",
        gender: order?.patientDetail?.gender || "",
        dateofbirth: order?.patientDetail?.dateofbirth || "",
      },
      products: order?.orderProducts?.map((product) => ({
        productId: product?.IID || "",
        productName: product?.productName || "",
        units: Number(product?.units) || 0,
        rxDays: Number(product?.rxDays) || 0,
        orderQty: Number(product?.orderQty) || 0,
        rxUnits: Number(product?.rxUnits) || 0,
        orderUnits: Number(product?.orderUnits) || 0,
        balanceUnits: Number(product?.balanceUnits) || 0,
        dosageNote: product?.dosageNote || "",
        mrp: Number(product?.mrp) || 0,
        discount: Number(product?.discount) || 0,
        gst: Number(product?.gst) || 0,
        netAmt: Number(product?.netAmt) || 0,
        type: product?.type || "",
        typeIndex: "",
        morningTime: Number(product?.morningTime) || 0,
        midDay: Number(product?.midDay) || 0,
        eveningTime: Number(product?.eveningTime) || 0,
        night: Number(product?.night) || 0,
        takenTime: product?.takenTime || "",
        takenTimeIndex: "",
        extra: Number(product?.extra) || 0,
        extraIndex: 0,
        drugs: product?.drugs || [],
        qty: Number(product?.qty) || 0,
        uom: product?.uom || "",
      })),
      delivery: {
        isAddress: order?.isAddress || false,
        isCollect: order?.isCollect || false,
        isClinic: order?.isClinic || false,
        address: {
          premisesNoFloor: order?.premisesNoFloor || "",
          premisesName: order?.premisesName || "",
          landmark: order?.landmark || "",
          areaRoad: order?.areaRoad || "",
          city: order?.city || "",
          pincode: order?.pincode || "",
          state: order?.state || "",
          phoneNumber2: order?.phoneNumber2 || "",
        },
      },
      payment: order?.payment || "",
      DID: order?.DID || "",
    };
    const response = await dispatch(createPdf(payload)).unwrap();
    if (response?.status === 200) {
      if (response?.data?.success && response?.data?.url) {
        const res = await fetch(response.data.url);
        payload.pdfPath = response?.data?.url;
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = payload?.patient?.name
          ? `${payload?.patient?.name} ${payload?.patient?.surname}_prescription.pdf`
          : `generated_file.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        // toast.success();
      }
    } else {
      toast.error(response?.message);
    }
  }, [dispatch]);
  useEffect(() => {
    let search;
    const searchOrders = async () => {
      if (debouncedSearchTerm?.length >= 3) {
        search = debouncedSearchTerm.toUpperCase();

        // if (searchPrefix) {
        try {
          const response = await dispatch(
            searchOrderById({
              debouncedSearchTerm: search?.trim(),
              currentPage,
              limit,
            })
          ).unwrap();

          if (response) {
            setFilteredData(response);
          } else {
            // Handle failure if needed
          }
        } catch (error) {
          console.error("Complete Error Object:", error);
        }
        // }
      } else {
        setFilteredData(data?.orders || []);
      }
    };

    searchOrders();
  }, [data?.orders, debouncedSearchTerm, currentPage, limit, dispatch]);

  const columns = useMemo(
    () => [
      {
        Header: "SN",
        accessor: (row, index) => {
          const serialNumber = (currentPage - 1) * limit + index + 1;
          return serialNumber;
        },
        sticky: "left",
        width: 60,
      },
      {
        Header: "Date",
        accessor: (row) =>
          row?.createdAt ? moment(row?.createdAt).format("ll") : null,
        sticky: "left",
        width: 140,
      },
      {
        Header: "OID",
        accessor: "OID",
        sticky: "left",
        width: 90,
      },
      {
        Header: "Patient Name",
        accessor: (row) => {
          return row?.patientDetail
            ? `${row?.patientDetail?.name} ${row?.patientDetail?.surname}`
            : null;
        },
        sticky: "left",
        width: 220,
      },
      {
        Header: "PID",
        accessor: (row) => row?.patientDetail?.PID,
        width: 100,
      },
      {
        Header: "Contact",
        accessor: (row) =>
          row?.patientDetail ? row?.patientDetail?.phoneNumber : null,
        width: 140,
      },
      {
        Header: "Doctor Name",
        accessor: (row) => {
          return `${row?.doctor?.personalInfo?.name || null}
          ${row?.doctor?.personalInfo?.surname || null}`;
        },
        width: 180,
      },
      {
        Header: "DID",
        accessor: (row) =>
          row?.doctor ? row?.doctor?.DID : null,
        width: 100,
      },
      // {
      //   Header: "Clinic Name",
      //   accessor: (row) => row?.doctor?.personalInfo?.clinicName,
      //   width: 180,
      // },
      {
        Header: "Invoice No.",
        accessor: (row) => row?.invoice?.invoiceNo,
        width: 120,
      },
      {
        Header: "Invoice Amt.",
        accessor: (row) => row?.invoice?.invoiceAmount,
        width: 120,
      },
      {
        Header: "Fee",
        accessor: (row) => {
          const value = row?.doctorOrderMargin?.marginPercentage;
          return value ? parseFloat(value).toFixed(2) : "";
        },
        width: 100,
      },
      {
        Header: "T1",
        accessor: (row) => row?.createdAt ? formatTime(row?.createdAt) : "",
        width: 100,
      },
      {
        Header: "T2",
        accessor: (row) => {
          if (row?.isCollect && row?.packedTime?.length > 0) {
            return formatTime(row?.packedTime);
          } else if (row?.dispatchTime?.length > 0) {
            return formatTime(row?.dispatchTime);
          }
          return "";
        },
        width: 100,
      },
      {
        Header: "T3",
        accessor: (row) => {
          if (row?.isCollect && row?.collectedTime?.length > 0) {
            return formatTime(row?.collectedTime);
          } else if (row?.deliveredTime?.length > 0) {
            return formatTime(row?.deliveredTime);
          }
          return "";
        },
        width: 100,
      },
      //{
      //  Header: "S1",
      //  accessor: (row) => row?.S1,
      //  width: 100,
      //},
      //{
      //  Header: "S2",
      //  accessor: (row) => row?.S2,
      //  width: 100,
      //},
      //{
      //  Header: "ET",
      //  accessor: (row) => row?.ET,
      //  width: 100,
      //},
      //{
      //  Header: "Payment",
      //  accessor: (row) => row?.invoice?.paymentType,
      //  width: 100,
      //},
      {
        Header: "Delivery",
        accessor: (row) =>
          row?.isClinic
            ? "Clinic"
            : row?.isCollect
              ? "Collect"
              : row?.isAddress
                ? "Address"
                : null,
        width: 100,
      },
      {
        Header: "Status",
        accessor: (row) => row?.orderStatus || null,
        width: 120,
      },
      {
        Header: "Rx Photo",
        accessor: (row) =>
          row?.filePath ? (
            <u
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={async () => {
                try {
                  const response = await fetch(row.filePath);
                  if (!response.ok) throw new Error("File download failed");

                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);

                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute("download", "Prescription");
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  console.error("Download failed", error);
                }
              }}
            >
              Download
            </u>
          ) : null,
        width: 130,
      },
      {
        Header: "ERx",
        accessor: (row) => {
          const isQuickOrder = !!row.QID;
          if (isQuickOrder) {
            return <span className="text-gray-400 text-xs italic font-medium">N/A (Q-Order)</span>;
          }
          return (
            <u
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={async () => {
                try {
                  if (row.pdfPath) {
                    const response = await fetch(row.pdfPath);
                    if (!response.ok) throw new Error("File download failed");

                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);

                    // Create a temporary link
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "DRx.pdf"); // Set the desired name here
                    document.body.appendChild(link);
                    link.click();
                    link.remove();

                    // Clean up the object URL
                    window.URL.revokeObjectURL(url);
                  } else {
                    handleCreatePdf(row)
                  }
                } catch (error) {
                  console.error("Download failed", error);
                }
              }}
            >
              Download
            </u>
          );
        },
        width: 130,
      },

      {
        Header: "Remark",
        accessor: (row) => row?.cancelReason || null,
        width: 140,
      },
    ],
    [currentPage, limit, handleCreatePdf]
  );

  return (
    <>
      {LoadingStatus === true ? <Loader loading={LoadingStatus} /> : ""}
      <div className="bg-gray-100 border border-primary-100 rounded-[8px] overflow-hidden">
        <div className="relative flex items-center ">
          <span className="h-[24px] w-[24px] absolute left-3">
            <Search />
          </span>
          <input
            type="text"
            placeholder="Search by OID/PID/DID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.trimStart())}
            className="pl-[42px] w-full border-none bg-transparent shadow-none outline-none placeholder:text-primary-400 h-[40px] placeholder:text-[16px]"
          />
          {searchTerm && (
            <span
              className="h-[20px] w-[20px] absolute right-5 cursor-pointer"
              onClick={() => setSearchTerm("")}
            >
              <Close />
            </span>
          )}
        </div>
        <AdminTable columns={columns} filteredData={filteredData} />

        {/* Pagination Component */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          isPreviousDisabled={currentPage === 1}
          isNextDisabled={currentPage === totalPages}
          itemLength={filteredData?.data?.length || filteredData?.length}
          totalItems={filteredData?.totalItems || data?.totalItems}
        />
      </div>
    </>
  );
};

export default Orders;
